import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';

interface ModelRecord {
  name: string;
  code: string;
}

interface ModelCheckboxFilterProps {
  apiBaseUrl: string;
  selectedModels: string[];
  onChange: (models: string[]) => void;
}

const ModelCheckboxFilter: React.FC<ModelCheckboxFilterProps> = ({
  apiBaseUrl,
  selectedModels,
  onChange,
}) => {
  const [models, setModels] = useState<ModelRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchModels = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${apiBaseUrl}/models`, {
          withCredentials: true,
        });
        setModels(response.data.models || []);
      } catch (error) {
        console.error('Error fetching models:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchModels();
  }, [apiBaseUrl]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleModel = (name: string) => {
    if (selectedModels.includes(name)) {
      onChange(selectedModels.filter((model) => model !== name));
    } else {
      onChange([...selectedModels, name]);
    }
  };

  const buttonLabel = () => {
    if (selectedModels.length === 0) return 'All';
    if (selectedModels.length === 1) return selectedModels[0];
    return `${selectedModels.length} selected`;
  };

  return (
    <div className="input-group input-group-sm flex-shrink-0" style={{ width: 'auto' }} ref={dropdownRef}>
      <span className="input-group-text">Model</span>
      <div className="dropdown position-relative" style={{ width: '100px' }}>
        <button
          type="button"
          className="form-select form-select-sm text-start text-truncate"
          style={{ width: '100px' }}
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-label="Filter by model"
          title={buttonLabel()}
        >
          {buttonLabel()}
        </button>
        {open && (
          <div className="dropdown-menu show p-2" style={{ minWidth: '220px' }}>
            <div className="d-flex justify-content-between px-1 mb-1">
              <button
                type="button"
                className="btn btn-sm btn-link p-0"
                onClick={() => onChange(models.map((model) => model.name))}
              >
                Select all
              </button>
              <button
                type="button"
                className="btn btn-sm btn-link p-0"
                onClick={() => onChange([])}
              >
                Clear
              </button>
            </div>
            <div className="dropdown-divider my-1" />
            {loading ? (
              <div className="text-center py-2">
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
              </div>
            ) : (
              <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                {models.map((model) => (
                  <div className="px-1 py-1" key={model.code}>
                    <div className="form-check mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`filter-model-${model.code}`}
                        checked={selectedModels.includes(model.name)}
                        onChange={() => toggleModel(model.name)}
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`filter-model-${model.code}`}
                      >
                        {model.name}
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ModelCheckboxFilter;
